import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import type {
  LaunchedCampaignApi,
  LaunchedCampaignsPaginatedResponse,
  ListLaunchedCampaignsParams,
} from '../utils/campaigns.mappers';
import type { CampaignStatus } from '../types/campaigns.types';
import type { SelfServiceCampaignDetail } from '../types/self-service-campaign.types';
import type { CampaignRequestAction } from '../utils/campaign-request-actions.utils';

const launchedCampaignsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getLaunchedCampaigns: builder.query<
    LaunchedCampaignsPaginatedResponse,
    ListLaunchedCampaignsParams | void
    >({
      query: (params) => ({
        method: 'GET',
        url: 'campaigns/',
        params: params ?? {},
        handleErrorGlobally: false,
      }),
      providesTags: [{ id: 'LIST', type: APITagType.LaunchedCampaigns }],
    }),
    getNewCampaignRequestCount: builder.query<{ count: number }, void>({
      query: () => ({
        method: 'GET',
        url: 'campaigns/new-requests/count/',
        handleErrorGlobally: false,
      }),
      providesTags: [{ id: 'NEW_COUNT', type: APITagType.LaunchedCampaigns }],
    }),
    getLaunchedCampaign: builder.query<LaunchedCampaignApi, string>({
      query: (campaignId) => ({
        method: 'GET',
        url: `campaigns/${campaignId}/`,
        handleErrorGlobally: false,
      }),
      providesTags: (_result, _error, campaignId) => [
        { id: campaignId, type: APITagType.LaunchedCampaigns },
      ],
    }),
    getSelfServiceCampaignDetail: builder.query<SelfServiceCampaignDetail, string>({
      query: (campaignId) => ({
        method: 'GET',
        url: `campaigns/${campaignId}/self-service/`,
        handleErrorGlobally: false,
      }),
    }),
    updateLaunchedCampaign: builder.mutation<
    LaunchedCampaignApi,
    {
      campaignId: string;
      action?: CampaignRequestAction;
      status?: CampaignStatus;
      clientId?: string | null;
    }
    >({
      query: ({ campaignId, action, status, clientId }) => ({
        method: 'PATCH',
        url: `campaigns/${campaignId}/`,
        data: {
          ...(action !== undefined ? { action } : {}),
          ...(status !== undefined ? { status } : {}),
          ...(clientId !== undefined ? { client_id: clientId } : {}),
        },
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { id: 'LIST', type: APITagType.LaunchedCampaigns },
        { id: 'NEW_COUNT', type: APITagType.LaunchedCampaigns },
        { id: campaignId, type: APITagType.LaunchedCampaigns },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetLaunchedCampaignsQuery,
  useGetNewCampaignRequestCountQuery,
  useGetLaunchedCampaignQuery,
  useGetSelfServiceCampaignDetailQuery,
  useUpdateLaunchedCampaignMutation,
} = launchedCampaignsEndpoints;

export function isPortalSyncNotConfiguredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const responseData = (error as { responseData?: Record<string, unknown> }).responseData;
  return responseData?.error_code === 'portal_sync_not_configured';
}
